import Clickable from 'components/Common/Clickable'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'
import CircleCheckboxInput from 'components/Luxkit/Checkbox/CircleCheckboxInput'
import cn from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'

const MIN_WIDTH = 120

const Tile = styled(Clickable)`
  --stroke-color: ${props => props.theme.palette.neutral.five};
  --stroke-width: 1px;

  gap: ${rem(2)};
  color: ${props => props.theme.palette.neutral.two};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: ${rem(92)};
  min-width: ${rem(MIN_WIDTH)};
  background-color: ${props => props.theme.palette.neutral.eight};
  border-radius: ${props => props.theme.borderRadius.large};
  padding: ${rem(8)};
  flex-shrink: 0;
  transition:
    .2s ease background-color,
    .2s ease color,
    .2s ease box-shadow;
  box-shadow: inset 0 0 0 var(--stroke-width) var(--stroke-color);

  &.is-tab {
    --stroke-color: transparent;
    --stroke-width: 0;
    height: auto;
  }

  &.is-ghost {
    background-color: transparent;
    --stroke-color: transparent;
  }

  &.is-active {
    --stroke-color: ${props => props.theme.palette.neutral.one};
    --stroke-width: 2px;
    color: ${props => props.theme.palette.neutral.one};
    &.is-tab {
      --stroke-color: transparent;
      --stroke-width: 0;
      background-color: ${props => props.theme.palette.neutral.six};
    }
  }

  ${mediaQueryUp.sm} {
    &:not(.is-active):hover {
      --stroke-color: ${props => props.theme.palette.neutral.one};
      color: ${props => props.theme.palette.neutral.one};
      &.is-tab {
        --stroke-color: transparent;
        background-color: ${props => props.theme.palette.neutral.seven};
      }
    }
  }

`

const StyledCheckbox = styled(CircleCheckboxInput)`
& :hover {
  cursor: pointer;
}
& > * {
  margin: 0;
}
`

interface Props {
  titleText: string,
  captionText?: string,
  icon?: React.ReactNode
  isActive?: boolean,
  onSelect?: (selection: string) => void
  selectionId: string
  showCheckbox?: boolean
  className?: string
  isTab?: boolean
}

/**
 * Helper function to update state when a Selection Tile has been selected
 * @param setState
 * @param selectionId
 */
export function updateSelection(setState: (value: React.SetStateAction<Set<string>>) => void, selectionId: string) {
  setState((currentState) => {
    if (currentState.has(selectionId)) {
      const newState = new Set(currentState)
      newState.delete(selectionId)
      return newState
    } else {
      const newState = new Set([...currentState, selectionId])
      return newState
    }
  })
}

function SelectionTile(props: Props) {
  const {
    titleText,
    captionText,
    isActive = false,
    onSelect,
    selectionId,
    showCheckbox = false,
    icon,
    className,
    isTab = false,
  } = props

  const handleTileClick = useCallback(() => {
    if (onSelect) {
      onSelect(selectionId)
    }
  }, [onSelect, selectionId])

  return (
    <Tile
    className={cn(className, { 'is-active': isActive, 'is-tab': isTab })}
    onClick={handleTileClick}
    data-testid={`selectionTile-${selectionId}`}
    >
      {icon}
      <BodyText variant="medium" weight={isActive ? 'bold' : 'normal'}>
        {titleText}
      </BodyText>
      <BodyText
        variant="small"
        align="center"
        wrap="word-wrap"
      >
        {captionText}
      </BodyText>
      {showCheckbox && <StyledCheckbox checked={isActive} tabIndex={-1} />}
    </Tile>
  )
}

export default SelectionTile
